<template>
  <FilterBadge v-bind="{ ...$props, ...$attrs }">
    {{ text }}
  </FilterBadge>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { useTranslation } from "@/language/translation";
import {
  SQDCEventActionTypeFieldType,
  SQDCEventTypeFieldRelation,
  useGetCostCentersForRainbowTileQuery,
  useHLPCategoriesQuery,
  useGetUserForRevisionListQuery,
  useRevisionTechnicalPlacesQuery,
} from "@/graphql";
import FilterBadge from "./FilterBadge.vue";
import type CustomAttributeFilter from "../../filterImplementations/CustomAttributeFilter";
import { FilterBadgeProps, type FilterEventActionTypes } from "../../FilterTypes";
import {
  formatSQDCButtonModuleValue,
  isSQDCButtonModuleType,
} from "@/modules/sqdc/helper/SQDCButtonModuleHelper";

const props = defineProps({
  filter: {
    type: Object as PropType<CustomAttributeFilter>,
    required: true,
  },
  ...FilterBadgeProps,
});
const { t } = useTranslation();

type CustomField = FilterEventActionTypes["fields"][number];

const customFields = computed(() => props.types.flatMap((type) => type.fields));
const customField = computed(() =>
  customFields.value.find((field) => field.id === props.filter.fieldId),
);
const customFieldNames = computed(
  () => new Map(customFields.value.map((field) => [field.id, t(`dynamic.${field.nameId}`)])),
);

const valueIds = computed(() => collectIds(props.filter.value));
const userIds = computed(() =>
  customField.value?.type === SQDCEventActionTypeFieldType.PERCENT_SHARING ||
  customField.value?.relation === SQDCEventTypeFieldRelation.USER
    ? valueIds.value
    : [],
);
const costCenterIds = computed(() =>
  customField.value?.relation === SQDCEventTypeFieldRelation.COST_CENTER ? valueIds.value : [],
);
const technicalPlaceIds = computed(() =>
  customField.value?.relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE ? valueIds.value : [],
);
const ideaCategoryIds = computed(() =>
  customField.value?.relation === SQDCEventTypeFieldRelation.IDEA_CATEGORY ? valueIds.value : [],
);

const { result: usersResult } = useGetUserForRevisionListQuery(
  () => ({ ids: userIds.value }),
  () => ({ enabled: userIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: costCentersResult } = useGetCostCentersForRainbowTileQuery(
  () => ({ ids: costCenterIds.value }),
  () => ({ enabled: costCenterIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: technicalPlacesResult } = useRevisionTechnicalPlacesQuery(
  () => ({ ids: technicalPlaceIds.value }),
  () => ({ enabled: technicalPlaceIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: ideaCategoriesResult } = useHLPCategoriesQuery();

const userNames = computed(
  () => new Map(usersResult.value?.Users.map((user) => [user.id, user.preferredName]) ?? []),
);
const costCenterNames = computed(
  () =>
    new Map(
      costCentersResult.value?.CostCenters.map((costCenter) => [
        costCenter.id,
        `${costCenter.path} ${costCenter.name}`,
      ]) ?? [],
    ),
);
const technicalPlaceNames = computed(
  () =>
    new Map(
      technicalPlacesResult.value?.OGTechnicalPlaces.map((technicalPlace) => [
        technicalPlace.id,
        `${technicalPlace.path} ${technicalPlace.name}`,
      ]) ?? [],
    ),
);
const ideaCategoryNames = computed(
  () =>
    new Map(
      ideaCategoriesResult.value?.HLPCategories.filter((category) =>
        ideaCategoryIds.value.includes(category.id),
      ).map((category) => [category.id, category.name]) ?? [],
    ),
);

function collectIds(value: unknown): number[] {
  const values = Array.isArray(value) ? value : [value];
  return [
    ...new Set(
      values
        .map((entry) => {
          if (typeof entry === "object" && entry != null) {
            const keyedEntry = entry as { id?: unknown; value?: unknown };
            return Number(keyedEntry.id ?? keyedEntry.value);
          }
          return Number(entry);
        })
        .filter(Number.isFinite),
    ),
  ];
}

function formatRelationValue(field: CustomField, value: unknown): string {
  const ids = collectIds(value);
  const nameMap =
    field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING ||
    field.relation === SQDCEventTypeFieldRelation.USER
      ? userNames.value
      : field.relation === SQDCEventTypeFieldRelation.COST_CENTER
        ? costCenterNames.value
        : field.relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE
          ? technicalPlaceNames.value
          : field.relation === SQDCEventTypeFieldRelation.IDEA_CATEGORY
            ? ideaCategoryNames.value
            : undefined;

  if (ids.length === 0 || nameMap == null) {
    return formatValue(value);
  }
  return ids.map((id) => nameMap.get(id) ?? id).join(", ");
}

function formatValue(value: unknown, field?: CustomField): string {
  if (field != null && isSQDCButtonModuleType(field.type)) {
    return String(formatSQDCButtonModuleValue(field.type, value, t));
  }
  if (
    field != null &&
    (field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING ||
      field.type === SQDCEventActionTypeFieldType.RELATION)
  ) {
    return formatRelationValue(field, value);
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object" && value != null) {
    const outageValue = value as { hasOutage?: boolean; dateRange?: unknown[] };
    if (typeof outageValue.hasOutage === "boolean") {
      const outageText = outageValue.hasOutage ? t("global.yes") : t("global.no");
      const dateText =
        outageValue.dateRange?.filter((date) => date != null && date !== "").join(" - ") ?? "";
      return dateText === "" ? outageText : `${outageText} | ${dateText}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

const text = computed(() => {
  const fieldName =
    customFieldNames.value.get(props.filter.fieldId) ?? `Custom Field ${props.filter.fieldId}`;
  return `${fieldName}: ${formatValue(props.filter.value, customField.value)}`;
});
</script>

<style lang="scss" scoped></style>
